import { useEffect, useState } from 'react';
import type { FilterBarFilterConfig, FilterValue } from './contracts';
import { formatDateRange } from './filterState';
import FilterChip from './FilterChip';

interface ConfiguredFilterChipProps {
  filter: FilterBarFilterConfig;
  selectedValue: FilterValue;
  expanded: boolean;
  controls?: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  onRemove: () => void;
}

const ConfiguredFilterChip = ({
  filter,
  selectedValue,
  expanded,
  controls,
  onClick,
  onRemove,
}: ConfiguredFilterChipProps) => {
  const rawValues = Array.isArray(selectedValue) ? selectedValue : selectedValue ? [selectedValue] : [];
  const [labels, setLabels] = useState<Record<string, string>>({});
  const resolveOption = filter.type === 'autosuggestion' ? filter.resolveOption : undefined;

  useEffect(() => {
    let active = true;
    const unresolvedValues = rawValues.filter(rawValue => !labels[rawValue]);

    if (!resolveOption || unresolvedValues.length === 0) {
      return () => {
        active = false;
      };
    }

    Promise.all(unresolvedValues.map(rawValue => resolveOption(rawValue)))
      .then(options => {
        if (!active) return;
        setLabels(current => ({
          ...current,
          ...Object.fromEntries(options.filter(Boolean).map(option => [option!.value, option!.label])),
        }));
      })
      .catch(() => {
        // Raw serialized values remain usable when their display labels cannot be loaded.
      });

    return () => {
      active = false;
    };
    // Values are primitives and the joined form prevents a new effect for an equivalent array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveOption, rawValues.join('|')]);

  const getLabel = (rawValue: string) => {
    if (labels[rawValue]) return labels[rawValue];
    if (filter.type === 'dropdown') {
      return filter.options.find(option => option.value === rawValue)?.label ?? rawValue;
    }
    return rawValue;
  };

  let displayValue = '';
  let additionalCount: number | undefined;

  if (filter.type === 'dateRange') {
    displayValue = formatDateRange(selectedValue);
  } else {
    if (rawValues[0]) displayValue = getLabel(rawValues[0]);
    if (rawValues.length > 1) additionalCount = rawValues.length - 1;
  }

  return (
    <FilterChip
      label={filter.label}
      value={displayValue}
      count={additionalCount}
      expanded={expanded}
      controls={controls}
      onClick={onClick}
      onRemove={onRemove}
    />
  );
};

export default ConfiguredFilterChip;
